import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Typography,
  Space,
  Skeleton,
  message,
} from "antd";
import {
  SmileOutlined,
  SaveOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import {
  getOrderSuccessPageSettings,
  updateOrderSuccessPageSettings,
} from "../../api/orderSuccessPageApi";

const { Text, Title } = Typography;
const { TextArea } = Input;

export default function OrderSuccessPageSettingsPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await getOrderSuccessPageSettings();
      form.setFieldsValue(data);
      setPreview(data);
    } catch {
      message.error("تعذر تحميل إعدادات صفحة نجاح الطلب");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const { data } = await updateOrderSuccessPageSettings(values);
      form.setFieldsValue(data);
      setPreview(data);
      message.success("تم حفظ الإعدادات بنجاح");
    } catch (err) {
      if (err?.errorFields) return; // فشل تحقق الفورم، مفيش داعي لرسالة إضافية
      message.error("حدث خطأ أثناء حفظ الإعدادات");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ direction: "rtl", maxWidth: 1100 }}>
      <Space align="center" style={{ marginBottom: 4 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SmileOutlined style={{ color: "#fff", fontSize: 20 }} />
        </div>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            صفحة نجاح الطلب
          </Title>
          <Text style={{ color: "#94A3B8", fontSize: 13 }}>
            تحكم في النصوص اللي بتظهر للعميل بعد إتمام الطلب بنجاح
          </Text>
        </div>
      </Space>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 0.9fr",
          gap: 24,
          marginTop: 24,
          alignItems: "start",
        }}
      >
        {/* ─── Form ─── */}
        <Card
          style={{ borderRadius: 14, border: "1px solid #E2E8F0" }}
          styles={{ body: { padding: 24 } }}
        >
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : (
            <Form
              form={form}
              layout="vertical"
              onValuesChange={(_, all) => setPreview((p) => ({ ...p, ...all }))}
            >
              <Form.Item
                name="title"
                label="العنوان الرئيسي"
                rules={[{ required: true, message: "العنوان مطلوب" }]}
              >
                <Input placeholder="تم استلام طلبك بنجاح! 🎉" maxLength={255} />
              </Form.Item>

              <Form.Item
                name="subtitle"
                label="النص الفرعي"
                rules={[{ required: true, message: "النص الفرعي مطلوب" }]}
              >
                <TextArea
                  rows={3}
                  placeholder="سيتواصل معك مندوب الشحن خلال يومين بإذن الله"
                />
              </Form.Item>

              <Form.Item
                name="show_order_number"
                label="إظهار رقم الطلب"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="primary_button_text"
                label="نص الزرار الأساسي"
                rules={[{ required: true, message: "النص مطلوب" }]}
              >
                <Input placeholder="تتبع الطلب" maxLength={100} />
              </Form.Item>

              <Form.Item
                name="secondary_button_text"
                label="نص الزرار الثانوي"
                rules={[{ required: true, message: "النص مطلوب" }]}
              >
                <Input placeholder="العودة للرئيسية" maxLength={100} />
              </Form.Item>

              {preview?.updated_at && (
                <Text style={{ color: "#94A3B8", fontSize: 12 }}>
                  آخر تحديث: {new Date(preview.updated_at).toLocaleString("ar-EG")}
                </Text>
              )}

              <div style={{ marginTop: 20 }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  onClick={handleSave}
                  style={{
                    background: "#6366F1",
                    borderColor: "#6366F1",
                    fontWeight: 600,
                  }}
                >
                  حفظ التعديلات
                </Button>
              </div>
            </Form>
          )}
        </Card>

        {/* ─── Live Preview ─── */}
        <Card
          title="معاينة"
          style={{ borderRadius: 14, border: "1px solid #E2E8F0" }}
          styles={{
            header: { border: "none", fontSize: 13, color: "#94A3B8" },
            body: { padding: 24 },
          }}
        >
          {loading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "32px 16px",
                background: "#F8FAFC",
                borderRadius: 12,
              }}
            >
              <CheckCircleFilled
                style={{ fontSize: 48, color: "#10B981", marginBottom: 16 }}
              />
              <Title level={4} style={{ margin: "0 0 8px" }}>
                {preview?.title || "تم استلام طلبك بنجاح! 🎉"}
              </Title>
              <Text style={{ color: "#64748B", display: "block", marginBottom: 16 }}>
                {preview?.subtitle ||
                  "سيتواصل معك مندوب الشحن خلال يومين بإذن الله"}
              </Text>

              {preview?.show_order_number && (
                <Text
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    padding: "4px 12px",
                    borderRadius: 20,
                    background: "#EEF2FF",
                    color: "#6366F1",
                    marginBottom: 20,
                  }}
                >
                  رقم الطلب: #12345
                </Text>
              )}

              <Space direction="vertical" style={{ width: "100%" }} size={8}>
                <Button
                  type="primary"
                  block
                  style={{ background: "#6366F1", borderColor: "#6366F1" }}
                >
                  {preview?.primary_button_text || "تتبع الطلب"}
                </Button>
                <Button block>
                  {preview?.secondary_button_text || "العودة للرئيسية"}
                </Button>
              </Space>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}